// ---------------------------------------------------------------------------
// Local wire types for the superuser-gated "ARV AI-Analysis Prompt Playground".
//
// Hand-authored rather than imported from `@backflip/api-types`, so the UI is
// not coupled to a `pnpm codegen` cycle. They mirror
// `packages/api-types/src/arv-ai-playground.ts` and `arv-prompt-templates.ts`
// exactly, including the bounds the UI enforces
// client-side and the provenance invariants the page needs to gate Run and Save.
// ---------------------------------------------------------------------------

/**
 * Root of the prompt-template query keys. Lives here (a module with no imports)
 * so `store/queryClient` can exclude it from localStorage persistence without
 * pulling the CF client into the client's own module graph.
 */
export const ARV_PROMPT_TEMPLATE_KEY_PREFIX = [
  "cf",
  "admin",
  "arv-prompt-templates",
] as const

/** buyer/flip or owner/refi flow. Mirrors `AI_ANALYSIS_FLOWS`. */
export type AiAnalysisFlow = "buyer" | "owner"

/** Both flows, for iterating the dual-flow coverage check (AC3.8). */
export const AI_ANALYSIS_FLOWS: readonly AiAnalysisFlow[] = ["buyer", "owner"]

/** Deterministic deal-shape variant. Mirrors `DEAL_FACT_VARIANTS`. */
export type DealFactVariant = "flip" | "rental" | "dscr" | "ambiguous"

/** Coarse flip-margin grade bucket. Mirrors `MARGIN_TIERS`. */
export type MarginTier =
  | "strong"
  | "good"
  | "thin"
  | "negative"
  | "price_unknown"

/**
 * Deterministic facts about the deal, passed to the AI so its narrative stays
 * grounded in the analyzer's math. Note the camelCase keys — these mirror the
 * server `DealFactsSchema` verbatim (it is NOT snake_cased on the wire).
 */
export interface DealFacts {
  variant: DealFactVariant
  marginTier: MarginTier
  flipWorks: boolean
  rentalWorks: boolean
}

// ---------------------------------------------------------------------------
// Two KINDS of warnings. Keeping them as distinct named aliases is deliberate:
// they carry opposite amounts of authority and are both plain `string[]`.
// ---------------------------------------------------------------------------

/**
 * Server-side ADVISORY heuristics about a template's output contract, returned
 * by save / activate / revise / per-version read.
 *
 * ⚠️ Prose pattern-matching with a measured 56% false-negative rate on dangerous
 * templates. An empty array means "nothing matched", NEVER "safe" — never render
 * it as an all-clear or use it as a gate.
 */
export type AdvisoryPromptWarnings = string[]

/**
 * Per-flow schema warnings from an ACTUAL generation. Unlike
 * `AdvisoryPromptWarnings` these describe what the model really emitted, which
 * is the only thing that exercises the output contract.
 */
export type RunSchemaWarnings = string[]

// ---------------------------------------------------------------------------
// Contract bounds the UI enforces client-side. Mirrors the exported constants
// in `packages/api-types/src/arv-prompt-templates.ts`.
//
// `ARV_PROMPT_REVISE_LATEST_OUTPUT_MAX_LENGTH` and
// `ARV_PROMPT_ADMIN_MAX_BODY_BYTES` are intentionally NOT mirrored: the server
// clamps/rejects on those and the UI has nothing to enforce against.
// ---------------------------------------------------------------------------

/** Hard ceiling on a saved template (and on a run's `system`), in characters. */
export const ARV_PROMPT_TEMPLATE_MAX_LENGTH = 20_000

/**
 * Below this, the cached prefix risks dropping under Anthropic's 1,024-token
 * prompt-cache minimum — `cache_control` is then silently ignored at full input
 * cost, with no error. Advisory: warn, never block.
 */
export const ARV_PROMPT_TEMPLATE_MIN_CACHE_SAFE_LENGTH = 1_500

/** Max length of the admin's per-version change note. */
export const ARV_PROMPT_TEMPLATE_NOTES_MAX_LENGTH = 500

/** How much template text the LIST endpoint returns per version. */
export const ARV_PROMPT_TEMPLATE_PREVIEW_LENGTH = 280

/** Max versions the LIST endpoint returns; older ones stay reachable by id. */
export const ARV_PROMPT_TEMPLATE_LIST_LIMIT = 50

/** Max length of the free-text (typed or dictated) revise feedback. */
export const ARV_PROMPT_REVISE_FEEDBACK_MAX_LENGTH = 4_000

/** Max assembled user content accepted by both run and revise. */
export const ARV_PROMPT_REVISE_USER_CONTENT_MAX_LENGTH = 8_000

// Preview-request bounds, mirrored from `ArvAiPromptPreviewRequestSchema` in
// `packages/api-types/src/arv-ai-playground.ts`. Anything looser here turns the
// UI's own hint into a 400 from the server.

/** Server: `address` is `z.string().min(3).max(500)`. */
export const ARV_PREVIEW_ADDRESS_MIN_LENGTH = 3
export const ARV_PREVIEW_ADDRESS_MAX_LENGTH = 500

/** Server: `sqft` is `z.number().int().positive().max(1_000_000)`. */
export const ARV_PREVIEW_SQFT_MAX = 1_000_000

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

/**
 * Read-only "derived inputs" panel data: the context the analyzer derives for a
 * subject before prompting the AI. None of these are editable in the UI.
 */
export interface ArvAiPromptDerivedVariables {
  zip: string | null
  comp_count: number
  comp_patterns: {
    count: number
    match_quality: string
    recency: string
    ppsf_spread: string
    /** The figures the prompt hands the model; null when unavailable. */
    price_range: { low: number; high: number } | null
    ppsf_range: { low: number; high: number } | null
    recency_window: {
      known: number
      last_6_months: number
      last_12_months: number
      oldest_months: number
    } | null
    proximity: {
      known: number
      within_half_mile: number
      within_one_mile: number
      range: { low: number; high: number }
    } | null
  }
  market_patterns: {
    available: boolean
    price_trend: string
    market_pace: string
    flip_returns: string
    flip_pace: string
  }
  cherre: {
    available: boolean
    year_built: number | null
    year_last_altered: number | null
    floor_count: number | null
    owner_type: string | null
    listing_status: string | null
    list_price: number | null
    last_sale: { year: number; amount: number } | null
  } | null
}

/** Request payload for `POST /api/arv-backtest/ai-analysis-preview`. */
export interface ArvAiPromptPreviewRequest {
  address: string
  sqft?: number
  force_fresh_comps?: boolean
  deal_facts: DealFacts
  flow: AiAnalysisFlow
}

/**
 * Response payload for `POST /api/arv-backtest/ai-analysis-preview`.
 *
 * `system` is whatever production would send: the ACTIVE template, or the code
 * constant when there is none. Server invariant:
 * `is_fallback_template === (template_id === null)`.
 */
export interface ArvAiPromptPreviewResponse {
  analysis_id: string
  system: string
  user_content: string
  /** The row id IS the version number; null when the fallback was used. */
  template_id: number | null
  is_fallback_template: boolean
  derived_variables: ArvAiPromptDerivedVariables
}

// ---------------------------------------------------------------------------
// Run
// ---------------------------------------------------------------------------

/**
 * Request payload for `POST /api/arv-backtest/ai-analysis-run`.
 *
 * NO generation knobs — the server pins production's model / temperature /
 * max_tokens so a run cannot drift from what users get. `deal_facts` is
 * required because the buyer margin tag is stamped from `deal_facts.marginTier`
 * exactly as production does (see `isPreviewStale` for the H10 hazard that
 * creates).
 */
export interface ArvAiPromptRunRequest {
  system: string
  user_content: string
  flow: AiAnalysisFlow
  deal_facts: DealFacts
}

/**
 * Response payload for `POST /api/arv-backtest/ai-analysis-run`. `tool_output`
 * is the production-faithful tool output when the per-flow schema passed, and
 * the model's raw stamped emission when it did NOT (which is what makes the
 * warnings diagnosable), so it stays `unknown` here.
 */
export interface ArvAiPromptRunResponse {
  tool_output: unknown
  warnings: RunSchemaWarnings
  model: string
}

// ---------------------------------------------------------------------------
// Prompt-template versions
// ---------------------------------------------------------------------------

/** One version in the history list — bounded preview, not full content. */
export interface ArvPromptTemplateSummary {
  /** Row id. This IS the version number (append-only, no separate column). */
  id: number
  /** Alias of `id`, spelled for the UI's version badge. */
  version: number
  is_active: boolean
  notes: string | null
  created_by: number | null
  created_at: string
  content_preview: string
  content_length: number
}

/** One version WITH its full text, from the per-version read. */
export interface ArvPromptTemplateDetail extends ArvPromptTemplateSummary {
  content: string
}

/**
 * Response for `GET /api/arv-backtest/prompt-templates/{id}`. Reading a
 * rollback candidate never activates it.
 */
export interface ArvPromptTemplateDetailResponse {
  template: ArvPromptTemplateDetail
  /** What it WOULD carry if made live. Advisory only — see the type's docs. */
  warnings: AdvisoryPromptWarnings
}

/** Response for `GET /api/arv-backtest/prompt-templates`. */
export interface ArvPromptTemplateListResponse {
  templates: ArvPromptTemplateSummary[]
  /** Computed across ALL versions, so it is correct even when truncated. */
  active_id: number | null
  has_more: boolean
}

/**
 * Save = create a new version AND make it live. There is no draft step: the
 * content is production's system prompt on the very next AI-analysis request.
 * Sent verbatim — never trim it, or the save gate's "this exact text ran"
 * comparison silently breaks.
 */
export interface ArvPromptTemplateSaveRequest {
  content: string
  notes?: string
}

/**
 * Shared response for both writes (save + activate). The write already happened
 * and is already live; `warnings` are ADVISORY after the fact, never a gate.
 */
export interface ArvPromptTemplateWriteResponse {
  template: ArvPromptTemplateSummary
  warnings: AdvisoryPromptWarnings
}

// ---------------------------------------------------------------------------
// Revise
// ---------------------------------------------------------------------------

/**
 * Request payload for `POST /api/arv-backtest/ai-analysis/revise`. Proposes a
 * full replacement template from the admin's feedback; saves nothing.
 */
export interface ArvPromptReviseRequest {
  /** Current editor text, verbatim. */
  template: string
  user_content?: string
  latest_output?: unknown
  /** Typed or dictated instruction. Untrusted free text on the server. */
  feedback: string
}

/** Response payload for `POST /api/arv-backtest/ai-analysis/revise`. */
export interface ArvPromptReviseResponse {
  /** Full proposed replacement (never a diff or fragment). */
  revised_template: string
  change_note: string
  warnings: AdvisoryPromptWarnings
}

// ---------------------------------------------------------------------------
// Provenance — what a preview was built from, and what a run actually ran.
//
// Both mutation hooks echo their request back alongside the response. The page
// needs the request that produced a result, not the form state at render time:
// the form can (and does) move between firing a mutation and reading its data.
// ---------------------------------------------------------------------------

/** The form fields a preview's `user_content` is only valid for (H10). */
export type ArvAiPromptPreviewInputs = Pick<
  ArvAiPromptPreviewRequest,
  "address" | "sqft" | "flow" | "deal_facts"
>

export interface ArvAiPromptPreviewResult {
  /** Exact request that baked `response.user_content`. */
  request: ArvAiPromptPreviewRequest
  response: ArvAiPromptPreviewResponse
}

/**
 * A COMPLETED run (schema warnings included — those are non-fatal and still
 * count as having exercised the template), tagged with the exact system text
 * and flow that produced it.
 */
export interface ArvAiPromptRunResult {
  request: ArvAiPromptRunRequest
  response: ArvAiPromptRunResponse
}

/**
 * H10: the run stamps the buyer margin tag from the REQUEST's `deal_facts`,
 * while the prose the model reads comes from a `user_content` baked by an
 * EARLIER preview — the server cannot detect the contradiction, so the page
 * must re-preview before Run. `force_fresh_comps` is excluded.
 */
export function isPreviewStale(
  previewedWith: ArvAiPromptPreviewInputs,
  current: ArvAiPromptPreviewInputs
): boolean {
  return (
    previewedWith.address !== current.address ||
    previewedWith.sqft !== current.sqft ||
    previewedWith.flow !== current.flow ||
    previewedWith.deal_facts.variant !== current.deal_facts.variant ||
    previewedWith.deal_facts.marginTier !== current.deal_facts.marginTier ||
    previewedWith.deal_facts.flipWorks !== current.deal_facts.flipWorks ||
    previewedWith.deal_facts.rentalWorks !== current.deal_facts.rentalWorks
  )
}

/**
 * EVERY completed run of this EXACT system text, oldest first — not the latest
 * per flow. Generation runs at temperature 1.0, so a marginal template flips
 * between passing and failing across identical runs; keeping all of them is what
 * stops a re-roll erasing the warnings an earlier run produced.
 */
export function runsWithSystem(
  runs: readonly ArvAiPromptRunResult[],
  system: string
): ArvAiPromptRunResult[] {
  return runs.filter((run) => run.request.system === system)
}

// ---------------------------------------------------------------------------
// Run ledger — the save gate's evidence, kept SEPARATE from the run list.
//
// The run list is display state and is capped, so filtering it for the save
// gate let a warned run be evicted by later clean re-rolls of the same text.
// The ledger accumulates per (system text, flow) instead, so a warning recorded
// once can never be laundered by re-running.
// ---------------------------------------------------------------------------

/** What EVERY run of one system text on one flow produced, taken together. */
export interface FlowRunSummary {
  flow: AiAnalysisFlow
  totalRuns: number
  warnedRuns: number
  /** Union across the flow's runs, first-seen order. Deduped for display only. */
  warnings: RunSchemaWarnings
}

/** All flows' evidence for one exact system text. */
export interface TemplateRunLedgerEntry {
  /** The exact `request.system` these runs used. */
  system: string
  /** Only flows that actually ran, in `AI_ANALYSIS_FLOWS` order. */
  flows: FlowRunSummary[]
}

/** Least-recently-run first, so eviction drops the front. */
export type TemplateRunLedger = readonly TemplateRunLedgerEntry[]

/**
 * Distinct system texts the ledger remembers. Re-running one text never evicts
 * anything (all its runs share an entry), so this only bounds how many DIFFERENT
 * templates one session can hold; clean entries are dropped before warned ones.
 */
export const MAX_LEDGER_TEMPLATES = 20

/** Warnings read defensively — the wire types are hand-mirrored, not parsed. */
function runWarnings(run: ArvAiPromptRunResult): RunSchemaWarnings {
  return Array.isArray(run.response.warnings) ? run.response.warnings : []
}

const entryHasWarnings = (entry: TemplateRunLedgerEntry): boolean =>
  entry.flows.some((flow) => flow.warnedRuns > 0)

/** Fold one run into a text's per-flow evidence, keeping `AI_ANALYSIS_FLOWS` order. */
function mergeRun(
  flows: readonly FlowRunSummary[],
  run: ArvAiPromptRunResult
): FlowRunSummary[] {
  const warnings = runWarnings(run)
  const existing = flows.find((flow) => flow.flow === run.request.flow)
  const merged: FlowRunSummary =
    existing === undefined
      ? {
          flow: run.request.flow,
          totalRuns: 1,
          warnedRuns: warnings.length > 0 ? 1 : 0,
          warnings: [...new Set(warnings)],
        }
      : {
          flow: existing.flow,
          totalRuns: existing.totalRuns + 1,
          warnedRuns: existing.warnedRuns + (warnings.length > 0 ? 1 : 0),
          warnings: [...new Set([...existing.warnings, ...warnings])],
        }
  const next = flows.filter((flow) => flow.flow !== merged.flow).concat(merged)
  return AI_ANALYSIS_FLOWS.flatMap((flow) =>
    next.filter((summary) => summary.flow === flow)
  )
}

/**
 * Record a completed run. The run's own text moves to the most-recent slot, so
 * it is never the eviction candidate; over the cap, the oldest CLEAN entry goes
 * first and only an all-warned ledger loses a warned one.
 */
export function recordRun(
  ledger: TemplateRunLedger,
  run: ArvAiPromptRunResult
): TemplateRunLedger {
  const system = run.request.system
  const existing = ledger.find((entry) => entry.system === system)
  const next: TemplateRunLedgerEntry[] = [
    ...ledger.filter((entry) => entry.system !== system),
    { system, flows: mergeRun(existing?.flows ?? [], run) },
  ]
  if (next.length <= MAX_LEDGER_TEMPLATES) return next
  const dropIndex = next.findIndex((entry) => !entryHasWarnings(entry))
  return next.filter((_, index) => index !== (dropIndex === -1 ? 0 : dropIndex))
}

/**
 * Save gate: per-flow evidence for this EXACT text, in `AI_ANALYSIS_FLOWS`
 * order. Empty ⇒ Save stays disabled; a missing flow ⇒ Save's confirm dialog
 * must name it (buyer is the only flow exercising the server-stamped margin
 * tag, so an owner-only validation can still 503 every buyer request).
 */
export function ledgerSummaries(
  ledger: TemplateRunLedger,
  system: string
): FlowRunSummary[] {
  return ledger.find((entry) => entry.system === system)?.flows ?? []
}

/**
 * Total read of a version id off the wire. Anything that is not a positive
 * integer degrades to null ("no saved version") so a schema-drifted response can
 * never render a fabricated version badge.
 */
export function readVersionId(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value > 0
    ? value
    : null
}

/**
 * Which saved version the editor is seeded from. Drift in EITHER provenance
 * field degrades to the built-in default rather than inventing a version.
 */
export function previewBaselineVersionId(
  response: ArvAiPromptPreviewResponse
): number | null {
  if (response.is_fallback_template) return null
  return readVersionId(response.template_id)
}

// ---------------------------------------------------------------------------
// Editor provenance — what the template textarea's text came from.
// ---------------------------------------------------------------------------

/**
 * What the editor was seeded from, so "modified" and the version badge stay
 * honest. A null `versionId` IS the built-in default, so an unreadable id can
 * never render as a version.
 */
export interface TemplateBaseline {
  text: string
  /** Row id of the version this text came from; null for the built-in default. */
  versionId: number | null
  /**
   * True only when a preview seeded this text. Deliberately loaded work — a
   * version pulled in from the history, or text this session published — is NOT
   * from a preview and must survive the next Analyze.
   */
  fromPreview: boolean
}

/**
 * May a fresh preview overwrite the editor? Only when the editor still holds
 * that same source's text, untouched. Keyed on PROVENANCE, not text-vs-baseline:
 * text loaded from history matches its baseline yet was never a preview's to
 * replace, and a deal-facts change forces a re-analyze that must not discard it.
 */
export function shouldReseedEditor(
  baseline: TemplateBaseline | null,
  editorText: string
): boolean {
  if (editorText.length === 0) return true
  if (baseline === null) return false
  return baseline.fromPreview && editorText === baseline.text
}

/**
 * The `sqft` override the server will actually accept, or undefined so the field
 * is omitted. Mirrors `z.number().int().positive().max(1_000_000)` — a fraction
 * or an out-of-range value is dropped here instead of 400ing at the server.
 */
export function parseSqftOverride(raw: string): number | undefined {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return undefined
  const parsed = Number(trimmed)
  if (!Number.isInteger(parsed)) return undefined
  return parsed > 0 && parsed <= ARV_PREVIEW_SQFT_MAX ? parsed : undefined
}

/** Why the server would reject this address, or null when it would accept it. */
export function addressValidationError(raw: string): string | null {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return "Address is required."
  if (trimmed.length < ARV_PREVIEW_ADDRESS_MIN_LENGTH) {
    return `Address must be at least ${ARV_PREVIEW_ADDRESS_MIN_LENGTH} characters.`
  }
  if (trimmed.length > ARV_PREVIEW_ADDRESS_MAX_LENGTH) {
    return `Address must be at most ${ARV_PREVIEW_ADDRESS_MAX_LENGTH.toLocaleString()} characters.`
  }
  return null
}
